import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";
import UserProfileUpdate from "./UserProfileUpdate";
import auth from "../../../firebase.init";
import Packages from "../Packages";
import { CircleUser, LayoutPanelTop, Package } from "lucide-react";
import PayPalAdmin from "./PayPalAdmin";
import LogoEditor from "./LogoEditor";
import BannerAdmin from "../../YouTubeThumbnailHome/Setting/BannerAdmin";
import FeaturesAdmin from "../../YouTubeThumbnailHome/Setting/FeaturesAdmin";
import AboutAdmin from "../../YouTubeThumbnailHome/Setting/AboutAdmin";
import FooterAdmin from "../../YouTubeThumbnailHome/Setting/FooterAdmin";
import GeminiKeys from "../../YouTubeThumbnailHome/Setting/GemeniKeys";
import ContactPageAdmin from "../../AiHomePage/Dashboard/ContactPageAdmin";
import TestimonialsAdmin from "./TestimonialsAdmin";
import FeatureTwoAdmin from "../../YouTubeThumbnailHome/Setting/FeatureTwoAdmin";

// Main Settings Home Component
const SettingsHome = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user] = useAuthState(auth);
  const [activeTab, setActiveTab] = useState("account");

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) return;

      try {
        setLoading(true);
        const response = await fetch(
          `https://server.enjoywiki.com/youtubeai-may/users?userEmail=${user.email}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const data = await response.json();
        // The API might return an array, so we take the first user object
        const userProfile = Array.isArray(data) ? data[0] : data;
        setUserData(userProfile);
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  // Render the active component based on tab selection
  const renderActiveComponent = () => {
    if (loading) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden"></span>
          </div>
        </div>
      );
    }

    if (!userData && !loading) {
      return (
        <div className="alert alert-warning" role="alert">
          User data could not be loaded. Please try again later.
        </div>
      );
    }

    switch (activeTab) {
      case "account":
        return <UserProfileUpdate userData={userData} />;
      case "LogoEditor":
        return <LogoEditor />;
      case "BannerAdmin":
        return <BannerAdmin />;
      case "AboutAdmin":
        return <AboutAdmin />;
      case "FeaturesAdmin":
        return <FeaturesAdmin />;
      case "FeatureTwoAdmin":
        return <FeatureTwoAdmin />;
      case "FooterAdmin":
        return <FooterAdmin />;
      case "GeminiKeys":
        return <GeminiKeys />;
      case "PayPalAdmin":
        return <PayPalAdmin />;
      case "TestimonialsAdmin":
        return <TestimonialsAdmin />;

      case "Packages":
        return <Packages />;
      default:
        return <UserProfileUpdate userData={userData} />;
    }
  };

  return (
    <div className="">
      <div className="row g-0">
        {/* Sidebar */}
        <div className="col-12 col-md-3 border-end bg-light">
          <div className="d-flex flex-column p-4">
            <div className="text-center mb-4">
              {loading ? (
                <div
                  className="spinner-border spinner-border-sm text-primary mb-3"
                  role="status"
                >
                  <span className="visually-hidden"></span>
                </div>
              ) : (
                <>
                  <img
                    src={
                      userData?.profileURL ||
                      user?.photoURL ||
                      "https://placehold.co/80"
                    }
                    alt="Profile"
                    className="rounded-circle mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                    }}
                  />
                  <h5 className="fw-bold text-dark mb-0">
                    {userData?.userName || user?.displayName || "User"}
                  </h5>
                  <p className="text-muted small">
                    {userData?.email || user?.email}
                  </p>
                </>
              )}
            </div>
            <div className="list-group list-group-flush">
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "account" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("account")}
              >
                <CircleUser className="me-2" /> Account
              </button>
              {/* LogoEditor */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "LogoEditor" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("LogoEditor")}
              >
                <LayoutPanelTop className="me-2" /> Navbar Options
              </button>
              {/* homeBanner */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "BannerAdmin" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("BannerAdmin")}
              >
                <LayoutPanelTop className="me-2" /> Banner Section
              </button>
              {/* All Tools Section */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "FeatureTwoAdmin" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("FeatureTwoAdmin")}
              >
                <LayoutPanelTop className="me-2" /> All Tools Section
              </button>



              {/* AboutAdmin */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "AboutAdmin" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("AboutAdmin")}
              >
                <LayoutPanelTop className="me-2" /> About Section
              </button>

              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "FeaturesAdmin" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("FeaturesAdmin")}
              >
                <LayoutPanelTop className="me-2" /> Featured Section
              </button>
 {/* Packages */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "Packages" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("Packages")}
              >
                <Package className="me-2" /> Packages
              </button>







              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "TestimonialsAdmin" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("TestimonialsAdmin")}
              >
                <LayoutPanelTop className="me-2" /> Testimonials
              </button>

              {/* GeminiKeys */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "GeminiKeys" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("GeminiKeys")}
              >
                <LayoutPanelTop className="me-2" /> Gemini Keys
              </button>
              {/* PayPalAdmin */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "PayPalAdmin" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("PayPalAdmin")}
              >
                <LayoutPanelTop className="me-2" /> Paypal Setup
              </button>

             
              {/* FooterAdmin */}
              <button
                className={`list-group-item list-group-item-action border-0 ${
                  activeTab === "FooterAdmin" ? "active fw-bold" : ""
                }`}
                onClick={() => setActiveTab("FooterAdmin")}
              >
                <LayoutPanelTop className="me-2" /> Footer Section
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="col-12 col-md-9">
          <div className="p-4">{renderActiveComponent()}</div>
        </div>
      </div>
    </div>
  );
};

export default SettingsHome;
